/**
 * 客户统计页面共用的辅助方法（客户概况 / 客户数据分布）
 *
 * 后端只返回「按维度值分组」的原始统计，这里负责把维度值翻译成展示用的名称：
 * 客户类型/来源/行业走字典，归属区域走行政区划表。
 */

import administrativeData from "@/assets/js/administrative.json";
import {dictName} from "@/views/h5/crm/crmDictionary";

/** 环形图/柱图取色，与商机统计的色系保持同一套视觉 */
export const PALETTE = ["#1677ff", "#13b8b8", "#52c41a", "#faad14", "#7a5af8", "#eb2f96", "#fa8c16", "#c3ccd9"];

/** 客户没填该维度时的展示文案 */
export const EMPTY_DIM_TEXT = "未填写";

/**
 * 省级行政区划列表，id 取前两位作为匹配前缀。
 *
 * ★ 不能直接用省码（510000）去匹配：客户表存的是完整行政区划编码，
 * 成都市是 510100，和省码 510000 只有前两位相同，按整串匹配一个都查不到。
 */
export const PROVINCE_LIST = (administrativeData || []).map(province => ({
    id: String(province.id).slice(0, 2),
    name: province.name,
}));

/**
 * 按区域码前两位取省名
 * @param {String} prefix 行政区划编码前两位，如 "51"
 * @returns {String} 省名，找不到时原样返回（避免图上出现空白项）
 */
export function provinceName(prefix) {
    if (!prefix) {
        return EMPTY_DIM_TEXT;
    }
    const hit = PROVINCE_LIST.find(province => province.id === String(prefix));
    return hit ? hit.name : String(prefix);
}

/**
 * 把后端的分组行转成图表/图例用的数据
 * @param {Array} rows 后端分组行 [{dimValue, customerCount|contactCount, ratio}]
 * @param {Array} [dictList] 字典列表（客户类型/来源/行业/职位），区域分布不传
 * @param {Function} [nameOf] 自定义名称解析（区域分布传 provinceName）
 * @param {String} [countField] 数量字段名，默认 customerCount；联系人分布传 contactCount
 * @returns {Array} [{name, value, ratio, color}]
 */
export function toChartRows(rows, dictList, nameOf, countField) {
    const field = countField || "customerCount";
    return (rows || []).map((row, index) => {
        const value = row.dimValue === null || row.dimValue === undefined ? "" : row.dimValue;
        let name;
        if (nameOf) {
            name = nameOf(value);
        } else if (value === "") {
            name = EMPTY_DIM_TEXT;
        } else {
            // 字典里查不到（字典项被删过）也按「未填写」展示，不留空白项
            name = dictName(dictList, value) || EMPTY_DIM_TEXT;
        }
        return {
            name,
            value: row[field] || 0,
            ratio: row.ratio || 0,
            color: PALETTE[index % PALETTE.length],
        };
    });
}
